import { useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { hieraApi } from "../lib/api";
import { docToMarkdown } from "../lib/docText";
import { t, type Lang } from "../lib/i18n";
import {
  PAGE_TINTS,
  pushRecentPage,
  readPageTints,
  readPinnedPages,
  readRecentPages,
  readSidePanelMode,
  readTreeClosed,
  readTreeFavOnly,
  readTreeSort,
  togglePinnedPage,
  writePageTint,
  writeSidePanelMode,
  writeTreeClosed,
  writeTreeFavOnly,
  writeTreeSort,
  type PageTint,
  type SidePanelMode,
  type TreeSort,
} from "../lib/prefs";
import { canEditRole } from "../lib/roles";
import { EmojiPicker } from "./EmojiPicker";
import { PageRelocateDialog } from "./PageRelocateDialog";
import { SpaceSettings } from "./SpaceSettings";

type PageNode = {
  id: string;
  parent_id: string | null;
  title: string;
  icon: string | null;
  sort_order: number;
  updated_at: string;
};

type DropPos = "before" | "after" | "into";

type CtxMenu = {
  pageId: string;
  title: string;
  x: number;
  y: number;
};

type Props = {
  lang: Lang;
  spaceId: string;
  pageId: string;
  onChanged: () => void;
};

function IconGear() {
  return (
    <svg className="nav-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M19.4 13a7.7 7.7 0 0 0 .05-2l2.02-1.57-2-3.46-2.4.97a7.6 7.6 0 0 0-1.73-1L12.9 2h-1.8L10.66 5a7.6 7.6 0 0 0-1.73 1l-2.4-.97-2 3.46L6.55 11a7.7 7.7 0 0 0 0 2l-2.02 1.57 2 3.46 2.4-.97c.54.42 1.12.76 1.73 1L11.1 22h1.8l.44-2.94c.61-.24 1.19-.58 1.73-1l2.4.97 2-3.46L19.4 13Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconHome() {
  return (
    <svg className="nav-ico" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg className="nav-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg className="nav-ico" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 4.5 19.5 9l-2.2.7-5.6 5.6-.7 2.2L6.5 13l2.2-.7 5.6-5.6L15 4.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="m9 15-4.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function isDescendant(pages: PageNode[], ancestorId: string, maybeChildId: string): boolean {
  const byId = new Map(pages.map((p) => [p.id, p]));
  let cur = byId.get(maybeChildId);
  while (cur?.parent_id) {
    if (cur.parent_id === ancestorId) return true;
    cur = byId.get(cur.parent_id);
  }
  return false;
}

function clampMenu(x: number, y: number, w = 260, h = 360): { x: number; y: number } {
  const maxX = Math.max(8, window.innerWidth - w - 8);
  const maxY = Math.max(8, window.innerHeight - h - 8);
  return { x: Math.min(Math.max(8, x), maxX), y: Math.min(Math.max(8, y), maxY) };
}

function tintClass(tint: PageTint | undefined): string {
  return tint ? ` is-tint-${tint}` : "";
}

export function SpaceNav({ lang, spaceId, pageId, onChanged }: Props) {
  const nav = useNavigate();
  const pageIdRef = useRef(pageId);
  pageIdRef.current = pageId;
  const filterRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<PageNode[]>([]);
  const [recentIds, setRecentIds] = useState<string[]>(() => readRecentPages(spaceId));
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => readPinnedPages(spaceId));
  const [tints, setTints] = useState<Record<string, PageTint>>(() => readPageTints(spaceId));
  const [sideMode, setSideMode] = useState<SidePanelMode>(() => readSidePanelMode(spaceId));
  const [sort, setSort] = useState<TreeSort>(() => readTreeSort());
  const [favOnly, setFavOnly] = useState(() => readTreeFavOnly());
  const [filterOpen, setFilterOpen] = useState(false);
  const [closed, setClosed] = useState<Set<string>>(() => new Set(readTreeClosed(spaceId).ids));
  const [canEdit, setCanEdit] = useState(false);
  const [picker, setPicker] = useState<{ id: string; top: number; left: number } | null>(null);
  const [settings, setSettings] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; pos: DropPos } | null>(null);
  const [ctx, setCtx] = useState<CtxMenu | null>(null);
  const [favIds, setFavIds] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState("");
  const [relocate, setRelocate] = useState<{ pageId: string; title: string; mode: "move" | "copy" } | null>(null);

  async function reload() {
    const res = await hieraApi.pages(spaceId);
    setPages(res.pages);
    setCanEdit(canEditRole(res.role));
    return res.pages;
  }

  useEffect(() => {
    setRecentIds(readRecentPages(spaceId));
    setPinnedIds(readPinnedPages(spaceId));
    setTints(readPageTints(spaceId));
    setSideMode(readSidePanelMode(spaceId));
    setClosed(new Set(readTreeClosed(spaceId).ids));
  }, [spaceId]);

  useEffect(() => {
    let dead = false;
    hieraApi.pages(spaceId).then((res) => {
      if (dead) return;
      const list = res.pages;
      setPages(list);
      setCanEdit(canEditRole(res.role));
      const parentIds = new Set(list.filter((p) => list.some((c) => c.parent_id === p.id)).map((p) => p.id));
      const stored = readTreeClosed(spaceId);
      const byId = new Map(list.map((p) => [p.id, p]));
      const next = new Set(
        stored.saved ? stored.ids.filter((id) => parentIds.has(id)) : [...parentIds],
      );
      let cur = byId.get(pageIdRef.current);
      while (cur?.parent_id) {
        next.delete(cur.parent_id);
        cur = byId.get(cur.parent_id);
      }
      setClosed(next);
      if (!stored.saved) writeTreeClosed(spaceId, [...next]);
    }).catch(() => {
      if (!dead) setPages([]);
    });
    hieraApi.home().then((home) => {
      if (dead) return;
      setFavIds(new Set(home.favorites.map((row) => String(row.page_id || "")).filter(Boolean)));
    }).catch(() => undefined);
    return () => {
      dead = true;
    };
  }, [spaceId]);

  useEffect(() => {
    if (!pageId || !pages.length) return;
    setClosed((prev) => {
      const byId = new Map(pages.map((p) => [p.id, p]));
      let cur = byId.get(pageId);
      if (!cur?.parent_id) return prev;
      let changed = false;
      const next = new Set(prev);
      while (cur?.parent_id) {
        if (next.delete(cur.parent_id)) changed = true;
        cur = byId.get(cur.parent_id);
      }
      if (changed) writeTreeClosed(spaceId, [...next]);
      return changed ? next : prev;
    });
  }, [pageId, pages, spaceId]);

  useEffect(() => {
    if (!pageId || !pages.some((row) => row.id === pageId)) return;
    setRecentIds(pushRecentPage(spaceId, pageId));
  }, [spaceId, pageId, pages]);

  useEffect(() => {
    if (!ctx) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setCtx(null);
    }
    function onPointer() {
      setCtx(null);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onPointer, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onPointer, true);
    };
  }, [ctx]);

  useEffect(() => {
    if (!filterOpen) return;
    function onPointer(e: globalThis.MouseEvent) {
      if (!filterRef.current?.contains(e.target as Node)) setFilterOpen(false);
    }
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [filterOpen]);

  const visibleIds = useMemo(() => {
    if (!favOnly) return null;
    const byId = new Map(pages.map((p) => [p.id, p]));
    const keep = new Set<string>();
    for (const id of favIds) {
      let cur = byId.get(id);
      while (cur) {
        keep.add(cur.id);
        cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
      }
    }
    return keep;
  }, [favOnly, favIds, pages]);

  const tree = useMemo(() => {
    const byParent = new Map<string | null, PageNode[]>();
    for (const p of pages) {
      if (visibleIds && !visibleIds.has(p.id)) continue;
      const k = p.parent_id;
      if (!byParent.has(k)) byParent.set(k, []);
      byParent.get(k)!.push(p);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => {
        if (sort === "name") return a.title.localeCompare(b.title, lang === "ru" ? "ru" : "en");
        if (sort === "updated") return +new Date(b.updated_at) - +new Date(a.updated_at);
        return a.sort_order - b.sort_order || a.title.localeCompare(b.title, lang === "ru" ? "ru" : "en");
      });
    }
    return byParent;
  }, [pages, sort, lang, visibleIds]);

  const recent = recentIds
    .map((id) => pages.find((row) => row.id === id))
    .filter((row): row is PageNode => Boolean(row));

  const pinned = pinnedIds
    .map((id) => pages.find((row) => row.id === id))
    .filter((row): row is PageNode => Boolean(row));

  function setMode(mode: SidePanelMode) {
    setSideMode(mode);
    writeSidePanelMode(spaceId, mode);
  }

  function toggle(id: string) {
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeTreeClosed(spaceId, [...next]);
      return next;
    });
  }

  async function createPage(parentId?: string) {
    if (parentId) {
      setClosed((prev) => {
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
    }
    const { page } = await hieraApi.createPage(spaceId, { title: "Untitled", parentId: parentId || null });
    const id = String(page.id || "");
    if (id) nav(`/p/${id}`);
  }

  function note(text: string) {
    setFlash(text);
    window.setTimeout(() => setFlash(""), 1400);
  }

  function openCtx(e: MouseEvent, page: PageNode) {
    e.preventDefault();
    e.stopPropagation();
    const pos = clampMenu(e.clientX, e.clientY);
    setCtx({ pageId: page.id, title: page.title || "Untitled", x: pos.x, y: pos.y });
  }

  async function copyInternalLink(id: string) {
    const url = `${window.location.origin}/p/${id}`;
    await navigator.clipboard.writeText(url);
    note(t(lang, "copied"));
    setCtx(null);
  }

  async function toggleFavorite(id: string) {
    const on = favIds.has(id);
    setFavIds((prev) => {
      const next = new Set(prev);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });
    try {
      if (on) await hieraApi.unfavorite(id);
      else await hieraApi.favorite(id);
    } catch {
      setFavIds((prev) => {
        const next = new Set(prev);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      });
    }
    setCtx(null);
  }

  function togglePin(id: string) {
    setPinnedIds(togglePinnedPage(spaceId, id));
    setCtx(null);
  }

  function applyTint(id: string, tint: PageTint | null) {
    setTints(writePageTint(spaceId, id, tint));
    setCtx(null);
  }

  async function exportMarkdown(id: string, title: string) {
    setCtx(null);
    const res = await hieraApi.page(id);
    const blob = new Blob(
      [`# ${res.page.title || title}\n\n${docToMarkdown(res.page.body)}\n`],
      { type: "text/markdown" },
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${res.page.title || title || "page"}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async function duplicatePage(id: string) {
    setCtx(null);
    const page = pages.find((row) => row.id === id);
    const res = await hieraApi.copyPage(id, {
      spaceId,
      parentId: page?.parent_id ?? null,
      titleSuffix: " copy",
    });
    await reload();
    onChanged();
    nav(`/p/${res.page.id}`);
  }

  function dropPosFromEvent(e: DragEvent<HTMLElement>, el: HTMLElement): DropPos {
    const box = el.getBoundingClientRect();
    const y = (e.clientY - box.top) / Math.max(1, box.height);
    if (y < 0.28) return "before";
    if (y > 0.72) return "after";
    return "into";
  }

  async function applyMove(sourceId: string, targetId: string, pos: DropPos) {
    if (sourceId === targetId) return;
    if (pos === "into" && isDescendant(pages, sourceId, targetId)) return;
    const target = pages.find((p) => p.id === targetId);
    if (!target) return;

    let body: { parentId: string | null; beforeId?: string | null; afterId?: string | null };
    if (pos === "into") {
      body = { parentId: targetId };
      setClosed((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    } else if (pos === "before") {
      body = { parentId: target.parent_id, beforeId: targetId };
    } else {
      body = { parentId: target.parent_id, afterId: targetId };
    }

    if (sort !== "order") {
      setSort("order");
      writeTreeSort("order");
    }

    try {
      await hieraApi.movePage(sourceId, body);
      await reload();
      onChanged();
    } catch {
      await reload();
    }
  }

  function renderPageRow(p: PageNode, opts: { tree?: boolean; guides?: boolean[]; kids?: PageNode[]; open?: boolean; last?: boolean }) {
    const kids = opts.kids || [];
    const open = opts.open ?? true;
    const last = opts.last ?? true;
    const guides = opts.guides || [];
    const stem = guides.map((cont) => (cont ? "│   " : "    ")).join("");
    const elbow = `${last ? "└" : "├"}${kids.length && !open ? "─▸ " : "── "}`;
    const drop = dropTarget?.id === p.id ? dropTarget.pos : null;
    return (
      <div key={p.id} className="tree-row">
        <div
          className={`tree-line${tintClass(tints[p.id])}${dragId === p.id ? " is-dragging" : ""}${drop ? ` is-drop-${drop}` : ""}`}
          draggable={canEdit && Boolean(opts.tree)}
          onContextMenu={(e) => openCtx(e, p)}
          onDragStart={(e) => {
            if (!canEdit || !opts.tree) return;
            e.dataTransfer.setData("text/hiera-page", p.id);
            e.dataTransfer.effectAllowed = "move";
            setDragId(p.id);
          }}
          onDragEnd={() => {
            setDragId(null);
            setDropTarget(null);
          }}
          onDragOver={(e) => {
            if (!canEdit || !opts.tree || !dragId || dragId === p.id) return;
            if (isDescendant(pages, dragId, p.id) && dropPosFromEvent(e, e.currentTarget) === "into") {
              return;
            }
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            const pos = dropPosFromEvent(e, e.currentTarget);
            setDropTarget((prev) => (prev?.id === p.id && prev.pos === pos ? prev : { id: p.id, pos }));
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDropTarget((prev) => (prev?.id === p.id ? null : prev));
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            const sourceId = e.dataTransfer.getData("text/hiera-page") || dragId;
            const pos = dropTarget?.id === p.id ? dropTarget.pos : dropPosFromEvent(e, e.currentTarget);
            setDragId(null);
            setDropTarget(null);
            if (!sourceId) return;
            void applyMove(sourceId, p.id, pos);
          }}
        >
          {opts.tree ? (
            kids.length ? (
              <button type="button" className="tree-elbow" aria-expanded={open} onClick={() => toggle(p.id)}>
                {stem}
                {elbow}
              </button>
            ) : (
              <span className="tree-elbow">
                {stem}
                {elbow}
              </span>
            )
          ) : pinnedIds.includes(p.id) ? (
            <span className="tree-pin-mark" aria-hidden="true">
              <IconPin />
            </span>
          ) : null}
          {canEdit || p.icon ? (
            <button
              type="button"
              className={`tree-emoji${p.icon ? "" : " is-empty"}`}
              aria-label={t(lang, "pickEmoji")}
              disabled={!canEdit}
              onClick={(e) => {
                const box = e.currentTarget.getBoundingClientRect();
                setPicker({ id: p.id, top: box.bottom + 4, left: Math.max(8, box.left - 8) });
              }}
            >
              {p.icon || "☺"}
            </button>
          ) : null}
          <NavLink to={`/p/${p.id}`} onContextMenu={(e) => openCtx(e, p)}>
            {p.title || "Untitled"}
          </NavLink>
          {canEdit && opts.tree ? (
            <button
              type="button"
              className="tree-add"
              aria-label={t(lang, "newPage")}
              onClick={() => void createPage(p.id)}
            >
              +
            </button>
          ) : null}
        </div>
        {opts.tree && kids.length && open ? renderNodes(p.id, [...guides, !last]) : null}
      </div>
    );
  }

  function renderNodes(parent: string | null, guides: boolean[]) {
    const nodes = tree.get(parent) || [];
    return nodes.map((p, index) => {
      const kids = tree.get(p.id) || [];
      const open = !closed.has(p.id);
      const last = index === nodes.length - 1;
      return renderPageRow(p, { tree: true, guides, kids, open, last });
    });
  }

  return (
    <>
      <nav className="side-tools">
        <NavLink to={`/s/${spaceId}`} end className={({ isActive }) => (isActive ? "is-active" : "")}>
          <IconHome />
          <span className="nav-label">{t(lang, "overview")}</span>
        </NavLink>
      </nav>
      <div className="side-mode" role="tablist" aria-label={t(lang, "sideMode")}>
        <button
          type="button"
          role="tab"
          aria-selected={sideMode === "workspace"}
          className={sideMode === "workspace" ? "is-on" : ""}
          onClick={() => setMode("workspace")}
        >
          {t(lang, "sideWorkspace")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sideMode === "recent"}
          className={sideMode === "recent" ? "is-on" : ""}
          onClick={() => setMode("recent")}
        >
          {t(lang, "sideRecent")}
        </button>
      </div>

      {sideMode === "recent" ? (
        <div className="side-recent-full">
          <div className="side-label">{t(lang, "recentOpens")}</div>
          {recent.length ? (
            recent.map((p) => renderPageRow(p, {}))
          ) : (
            <p className="side-empty">{t(lang, "recentEmpty")}</p>
          )}
        </div>
      ) : (
        <>
          {pinned.length ? (
            <div className="side-pinned">
              <div className="side-label">{t(lang, "pinned")}</div>
              {pinned.map((p) => renderPageRow(p, {}))}
            </div>
          ) : null}
          <div className="side-label pages-head">
            {canEdit ? (
              <button
                type="button"
                className="pages-gear"
                title={t(lang, "spaceSettings")}
                aria-label={t(lang, "spaceSettings")}
                onClick={() => setSettings(true)}
              >
                <IconGear />
              </button>
            ) : null}
            <span>{t(lang, "pages")}</span>
            {canEdit ? (
              <button
                type="button"
                className="pages-add"
                title={t(lang, "newPage")}
                aria-label={t(lang, "newPage")}
                onClick={() => void createPage()}
              >
                +
              </button>
            ) : null}
            {flash ? <span className="side-flash" role="status">{flash}</span> : null}
            <div className="tree-filter" ref={filterRef}>
              <button
                type="button"
                className={`tree-filter-btn${filterOpen || favOnly || sort !== "order" ? " is-on" : ""}`}
                title={t(lang, "pageFilters")}
                aria-label={t(lang, "pageFilters")}
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <IconFilter />
              </button>
              {filterOpen ? (
                <div className="tree-filter-menu" role="menu">
                  <div className="tree-filter-caption">{t(lang, "sortLabel")}</div>
                  {(
                    [
                      ["order", "sortOrder"],
                      ["name", "sortName"],
                      ["updated", "sortUpdated"],
                    ] as const
                  ).map(([value, key]) => (
                    <button
                      key={value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={sort === value}
                      className={sort === value ? "is-on" : ""}
                      onClick={() => {
                        setSort(value);
                        writeTreeSort(value);
                      }}
                    >
                      {t(lang, key)}
                    </button>
                  ))}
                  <div className="tree-filter-sep" />
                  <button
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={favOnly}
                    className={favOnly ? "is-on" : ""}
                    onClick={() => {
                      const next = !favOnly;
                      setFavOnly(next);
                      writeTreeFavOnly(next);
                    }}
                  >
                    {t(lang, "filterFavorites")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <div className="tree">{renderNodes(null, [])}</div>
        </>
      )}

      {ctx ? (
        <div
          className="ctx-menu page-ctx"
          style={{ left: ctx.x, top: ctx.y }}
          role="menu"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button type="button" className="ctx-item" role="menuitem" onClick={() => void copyInternalLink(ctx.pageId)}>
            {t(lang, "copyLink")}
          </button>
          <button type="button" className="ctx-item" role="menuitem" onClick={() => togglePin(ctx.pageId)}>
            {pinnedIds.includes(ctx.pageId) ? t(lang, "unpinPage") : t(lang, "pinPage")}
          </button>
          <button type="button" className="ctx-item" role="menuitem" onClick={() => void toggleFavorite(ctx.pageId)}>
            {favIds.has(ctx.pageId) ? t(lang, "removeFavorite") : t(lang, "addFavorite")}
          </button>
          <div className="ctx-swatches" role="group" aria-label={t(lang, "pageColor")}>
            <button
              type="button"
              className={`ctx-swatch is-clear${tints[ctx.pageId] ? "" : " is-on"}`}
              title={t(lang, "pageColorClear")}
              aria-label={t(lang, "pageColorClear")}
              onClick={() => applyTint(ctx.pageId, null)}
            />
            {PAGE_TINTS.map((tint) => (
              <button
                key={tint}
                type="button"
                className={`ctx-swatch is-tint-${tint}${tints[ctx.pageId] === tint ? " is-on" : ""}`}
                title={t(lang, "pageColor")}
                aria-label={tint}
                onClick={() => applyTint(ctx.pageId, tint)}
              />
            ))}
          </div>
          <button
            type="button"
            className="ctx-item"
            role="menuitem"
            onClick={() => void exportMarkdown(ctx.pageId, ctx.title)}
          >
            {t(lang, "exportMdFull")}
          </button>
          {canEdit ? (
            <>
              <button
                type="button"
                className="ctx-item"
                role="menuitem"
                onClick={() => void duplicatePage(ctx.pageId)}
              >
                {t(lang, "duplicate")}
              </button>
              <button
                type="button"
                className="ctx-item"
                role="menuitem"
                onClick={() => {
                  setRelocate({ pageId: ctx.pageId, title: ctx.title, mode: "move" });
                  setCtx(null);
                }}
              >
                {t(lang, "move")}…
              </button>
              <button
                type="button"
                className="ctx-item"
                role="menuitem"
                onClick={() => {
                  setRelocate({ pageId: ctx.pageId, title: ctx.title, mode: "copy" });
                  setCtx(null);
                }}
              >
                {t(lang, "copyTo")}…
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {relocate ? (
        <PageRelocateDialog
          lang={lang}
          pageId={relocate.pageId}
          pageTitle={relocate.title}
          sourceSpaceId={spaceId}
          initialMode={relocate.mode}
          onClose={() => setRelocate(null)}
          onDone={({ mode, pageId: nextId, spaceId: nextSpace }) => {
            setRelocate(null);
            onChanged();
            if (mode === "copy" || nextSpace !== spaceId) {
              nav(`/p/${nextId}`);
              return;
            }
            void reload();
          }}
        />
      ) : null}
      {settings ? (
        <SpaceSettings
          lang={lang}
          spaceId={spaceId}
          onClose={() => setSettings(false)}
          onChanged={() => {
            void reload();
            onChanged();
          }}
          onDeleted={() => {
            setSettings(false);
            onChanged();
            nav("/");
          }}
        />
      ) : null}
      {picker ? (
        <EmojiPicker
          lang={lang}
          top={picker.top}
          left={picker.left}
          onClose={() => setPicker(null)}
          onPick={(emoji) => {
            const id = picker.id;
            setPicker(null);
            setPages((prev) => prev.map((row) => (row.id === id ? { ...row, icon: emoji || null } : row)));
            void hieraApi.patchPage(id, { icon: emoji });
          }}
        />
      ) : null}
    </>
  );
}
